/**
 * Enhetshälsa: låg batterispänning, urkopplade enheter och datafördröjning i ett kort.
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Battery0Icon, BoltSlashIcon, ClockIcon } from '@heroicons/react/24/outline';
import api from '@/services/api/client';
import { assignmentApi } from '@/services/assignments/assignmentApi';
import { useLanguage, useTranslation } from '@/store/languageStore';
import { useAuthStore } from '@/store/authStore';
import { isGlobalAdminUser } from '@/utils/isGlobalAdminUser';
import DeviceHealthDetail, { type DeviceHealthKind } from './DeviceHealthDetail';

interface CountResponse {
  data?: unknown[];
  meta?: { dumpingCount?: number; totalCount?: number };
}

interface UnpluggedRow {
  vehicleId: number;
}

function useCount(endpoint: string, params: Record<string, string | number>, enabled = true) {
  return useQuery(
    ['dashboard-widget', endpoint, JSON.stringify(params)],
    async () => {
      const response = await api.get<CountResponse & { success: boolean }>(endpoint, { params });
      if (!response.data.success) throw new Error('Failed to load data');
      return response.data;
    },
    { staleTime: 60 * 1000, enabled }
  );
}

function HealthRow({
  icon,
  iconClass,
  title,
  hint,
  value,
  onClick,
}: {
  icon: React.ReactNode;
  iconClass: string;
  title: string;
  hint: string;
  value: string;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full min-w-0 items-center gap-3 rounded-lg py-2 text-left hover:bg-bgHover"
      aria-label={t('actionRequired.openDetail').replace('{name}', title)}
    >
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconClass}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-textDefault">{title}</div>
        <div className="truncate text-xs text-textSecondary">{hint}</div>
      </div>
      <div className="shrink-0 text-3xl font-semibold tabular-nums text-textDefault">{value}</div>
    </button>
  );
}

const DeviceHealthWidget: React.FC = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const queryClient = useQueryClient();
  const [openKind, setOpenKind] = useState<DeviceHealthKind | null>(null);
  const locale = language === 'en' ? 'en-GB' : language === 'lt' ? 'lt-LT' : 'sv-SE';
  const isGlobalAdmin = isGlobalAdminUser(useAuthStore((state) => state.user));
  const format = new Intl.NumberFormat(locale);

  const battery = useCount('/api/dashboard/data/low-battery-vehicles', { threshold: 12 });
  const unplugged = useCount('/api/dashboard/data/unplugged-vehicles', {});
  const lag = useCount('/api/dashboard/data/gps-data-lag', { min: 60, mode: 'dump-only' }, isGlobalAdmin);

  const unpluggedRows = Array.isArray(unplugged.data?.data) ? (unplugged.data?.data as UnpluggedRow[]) : [];
  const unpluggedIds = unpluggedRows
    .map((row) => row?.vehicleId)
    .filter((id) => Number.isFinite(id));
  const assigned = useQuery(
    ['device-health-assigned', unpluggedIds.join(',')],
    () => assignmentApi.getOpenByVehicleIds(unpluggedIds, 'UNPLUGGED_DEVICE'),
    { enabled: unpluggedIds.length > 0, staleTime: 60 * 1000 }
  );

  const numberOrDash = (loading: boolean, failed: boolean, value: number) => {
    if (loading) return '–';
    if (failed) return '–';
    return format.format(value);
  };

  const assignedCount = assigned.data ? Object.keys(assigned.data).length : 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <h2 className="text-base font-semibold text-textDefault">{t('deviceHealth.title')}</h2>
      <div className="mt-2 flex min-h-0 flex-1 flex-col justify-center divide-y divide-borderDefault">
        <HealthRow
          icon={<Battery0Icon className="h-5 w-5" />}
          iconClass="bg-amber-500/15 text-amber-400"
          title={t('deviceHealth.lowBattery')}
          hint={t('deviceHealth.lowBatteryHint')}
          value={numberOrDash(battery.isLoading, battery.isError, battery.data?.data?.length ?? battery.data?.meta?.totalCount ?? 0)}
          onClick={() => setOpenKind('lowBattery')}
        />
        <HealthRow
          icon={<BoltSlashIcon className="h-5 w-5" />}
          iconClass="bg-rose-500/15 text-rose-400"
          title={t('deviceHealth.unplugged')}
          hint={t('deviceHealth.unpluggedAssigned').replace('{{count}}', String(assigned.isLoading ? 0 : assignedCount))}
          value={numberOrDash(unplugged.isLoading, unplugged.isError, unplugged.data?.data?.length ?? unplugged.data?.meta?.totalCount ?? 0)}
          onClick={() => setOpenKind('unplugged')}
        />
        {isGlobalAdmin && (
          <HealthRow
            icon={<ClockIcon className="h-5 w-5" />}
            iconClass="bg-teal-500/15 text-teal-300"
            title={t('deviceHealth.dataLag')}
            hint={t('deviceHealth.dataLagHint')}
            value={numberOrDash(
              lag.isLoading,
              lag.isError,
              lag.data?.meta?.dumpingCount ?? lag.data?.data?.length ?? 0
            )}
            onClick={() => setOpenKind('dataLag')}
          />
        )}
      </div>
      {openKind && (
        <DeviceHealthDetail
          kind={openKind}
          onClose={() => {
            setOpenKind(null);
            void queryClient.invalidateQueries(['device-health-assigned']);
            void queryClient.invalidateQueries(['dashboard-widget']);
          }}
        />
      )}
    </div>
  );
};

export default DeviceHealthWidget;
